import { useState, useCallback, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Activity, AlertTriangle, CheckCircle, ClipboardCheck, GalleryVerticalEnd, ListChecks, Search, XCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CardTableViewToggle } from '@/components/ui/card-table-view-toggle'
import { getStatusPillClass } from '@/features/recover-requests/components/RecoveryTrackingHeader'
import type { MobilisationTeam, TechnicalCheckRow } from '../../api'
import { useTechnicalTaskActionsBySet, useSaveTechnicalTaskActionBySet } from '../../hooks'

const STATUS_OPTIONS = ['Pending', 'In Progress', 'Completed', 'Issue'] as const

/** Map UI status to API value (Pending -> No). */
function statusToApi(s: string): string {
    return s === 'Pending' ? 'No' : s
}
/** Map API status to UI value (No -> Pending). */
function statusFromApi(s: string): string {
    const v = (s ?? '').trim()
    return v === 'No' ? 'Pending' : v
}

/** Renders the task-actions list icon only when the check has at least one recorded action. */
function TechnicalTaskActionsIconButton({
    setId,
    recoveryTaskId,
    onClick,
    title,
}: {
    setId: number | null
    recoveryTaskId: number
    onClick: () => void
    title: string
}) {
    const { data: actions = [] } = useTechnicalTaskActionsBySet(setId, recoveryTaskId)
    if (actions.length === 0) return null
    return (
        <Button variant="ghost" className="h-8 w-8 p-0" onClick={onClick} title={title}>
            <GalleryVerticalEnd className="h-4 w-4" />
        </Button>
    )
}

/** Team status for sidebar dot: grey pending, red issue, green all OK */
function getChecklistTeamStatusDotClass(ok: number, issues: number, total: number): string {
    if (total === 0) return 'bg-muted-foreground/50'
    if (issues > 0) return 'bg-destructive'
    if (ok === total) return 'bg-green-500'
    return 'bg-muted-foreground/50'
}

/** Shared left sidebar: list of teams with selection, status dot, and progress */
function TechnicalChecklistSidebar({
    filteredTeams,
    selectedTeamId,
    onSelectTeam,
    getCheckStats,
    t,
}: {
    filteredTeams: MobilisationTeam[]
    selectedTeamId: number | null
    onSelectTeam: (teamId: number) => void
    getCheckStats: (team: MobilisationTeam) => { ok: number; issues: number; pending: number; total: number }
    t: (key: string, fallback?: string) => string
}) {
    return (
        <div className="w-72 shrink-0 flex flex-col border-r border-border bg-muted/20">
            <div className="shrink-0 px-3 py-2.5 border-b border-border bg-muted/50 font-semibold text-xs uppercase tracking-wider text-muted-foreground">
                {t('recoverRequests.mobilisation.teams', 'Teams')} ({filteredTeams.length})
            </div>
            <div className="flex-1 overflow-y-auto py-1">
                {filteredTeams.map((team) => {
                    const { ok, issues, total } = getCheckStats(team)
                    const isSelected = selectedTeamId === team.id
                    const pct = total > 0 ? Math.round((ok / total) * 100) : 0
                    const dotClass = getChecklistTeamStatusDotClass(ok, issues, total)
                    return (
                        <button
                            key={team.id}
                            type="button"
                            onClick={() => onSelectTeam(team.id)}
                            className={cn(
                                'w-full flex items-start gap-2 px-3 py-2.5 text-left transition-colors border-l-2',
                                isSelected ? 'bg-primary/10 border-primary' : 'hover:bg-muted/50 border-transparent'
                            )}
                        >
                            <span className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', dotClass)} title={total === 0 ? t('recoverRequests.mobilisation.noChecks', 'No checks') : issues > 0 ? t('recoverRequests.mobilisation.issuesFound', 'Issues') : ok === total ? t('recoverRequests.mobilisation.ok', 'OK') : t('recoverRequests.mobilisation.pending', 'Pending')} />
                            <div className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-medium text-foreground">{team.teamName ?? '—'}</span>
                                <div className="mt-1 flex items-center gap-2">
                                    <div className="h-1 flex-1 min-w-0 overflow-hidden rounded-full bg-muted">
                                        <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                                    </div>
                                    <span className="shrink-0 text-xs font-mono text-muted-foreground">{ok}/{total}</span>
                                </div>
                            </div>
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

/** Left border colour for checklist board cards: No=grey, In Progress=blue, Completed=green, Issue=red */
function getChecklistCardBorderClass(status: string): string {
    const s = (status ?? 'No').trim()
    if (s === 'In Progress') return 'border-l-blue-500'
    if (s === 'Completed') return 'border-l-green-500'
    if (s === 'Issue') return 'border-l-red-500'
    return 'border-l-slate-400'
}

function ChecklistBoardCard({
    check,
    getEditState,
    setCheckEdit,
    handleSave,
    hasUnsavedChanges,
    saveMutation,
    canEdit,
    setActionsTaskId,
    getStatusPillClass,
    t,
    setId,
}: {
    check: TechnicalCheckRow
    getEditState: (c: TechnicalCheckRow) => { status: string; comments: string }
    setCheckEdit: (c: TechnicalCheckRow, field: 'status' | 'comments', value: string) => void
    handleSave: (c: TechnicalCheckRow) => void
    hasUnsavedChanges: (c: TechnicalCheckRow) => boolean
    saveMutation: { isPending: boolean; variables?: { recoveryTaskId?: number } }
    canEdit: boolean
    setActionsTaskId: (id: number | null) => void
    getStatusPillClass: (s: string) => string
    t: (key: string, fallback?: string) => string
    setId: number | null
}) {
    const state = getEditState(check)
    const taskId = check.recoveryTaskId ?? check.id
    const isSaving = saveMutation.isPending && saveMutation.variables?.recoveryTaskId === taskId
    const savedStatus = (check.status ?? 'No').trim()
    const borderClass = getChecklistCardBorderClass(savedStatus)
    return (
        <div
            className={cn(
                'flex flex-col gap-2 rounded-md border border-l-4 border-table-border bg-card p-4 font-sans antialiased text-sm',
                'transition-[transform,box-shadow] duration-150 ease',
                'shadow-[0_1px_3px_rgba(0,0,0,0.05)] hover:-translate-y-0.5 hover:shadow-[0_8px_16px_rgba(0,0,0,0.08)]',
                borderClass
            )}
        >
            <div className="font-medium text-foreground">{check.check}</div>
            <div>
                {canEdit ? (
                    <Select value={state.status} onValueChange={(v) => setCheckEdit(check, 'status', v)}>
                        <SelectTrigger className={cn('h-8 w-[140px] rounded-full border-0 py-1 font-medium focus:ring-2', getStatusPillClass(state.status ?? 'Pending'))}>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {STATUS_OPTIONS.map((opt) => (
                                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                ) : (
                    <span className={cn('inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap', getStatusPillClass(check.status ?? 'No'))}>
                        {statusFromApi(check.status ?? 'No')}
                    </span>
                )}
            </div>
            {canEdit ? (
                <Textarea
                    className="min-h-[60px] resize-y text-sm font-sans"
                    value={state.comments}
                    onChange={(e) => setCheckEdit(check, 'comments', e.target.value)}
                    placeholder={t('recoverRequests.mobilisation.commentsPlaceholder', 'Comments')}
                    rows={2}
                />
            ) : (
                <p className="text-sm text-muted-foreground">{state.comments || '—'}</p>
            )}
            {check.completedBy && (
                <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 font-mono text-xs font-medium text-foreground">
                        {(check.completedBy ?? '?').slice(0, 1).toUpperCase()}
                    </span>
                    {check.completedBy}
                </span>
            )}
            {canEdit && (
                <div className="flex items-center gap-2 pt-1" onClick={(e) => e.stopPropagation()}>
                    {hasUnsavedChanges(check) && (
                        <Button size="sm" variant="default" onClick={() => handleSave(check)} disabled={isSaving}>
                            {t('common.save', 'Save')}
                        </Button>
                    )}
                    <TechnicalTaskActionsIconButton
                        setId={setId}
                        recoveryTaskId={taskId}
                        onClick={() => setActionsTaskId(taskId)}
                        title={t('recoverRequests.mobilisation.taskActions', 'Task actions')}
                    />
                </div>
            )}
        </div>
    )
}

interface Step4TechnicalChecklistProps {
    teams: MobilisationTeam[]
    setId?: number
    onOpenImpactAssessment?: () => void
}

export function Step4TechnicalChecklist({ teams, setId, onOpenImpactAssessment }: Step4TechnicalChecklistProps) {
    const { t } = useTranslation()
    const [actionsTaskId, setActionsTaskId] = useState<number | null>(null)
    const [editState, setEditState] = useState<Record<number, { status: string; comments: string }>>({})
    const [search, setSearch] = useState('')
    const [filter, setFilter] = useState<'all' | 'issues' | 'ok'>('all')
    const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null)
    const [viewMode, setViewMode] = useState<'card' | 'table'>('table')

    const saveMutation = useSaveTechnicalTaskActionBySet(setId ?? null)
    const canEdit = setId != null && setId > 0

    const stats = useMemo(() => {
        let okCount = 0
        let issueCount = 0
        let pendingCount = 0
        let inProgressCount = 0
        teams.forEach((team) => {
            (team.checks ?? []).forEach((c) => {
                if (c.result === 'ok') okCount++
                else if (c.result === 'issue') issueCount++
                else pendingCount++
                const status = (c.status ?? '').trim()
                if (status === 'In Progress') inProgressCount++
            })
        })
        const totalChecks = okCount + issueCount + pendingCount
        return { ok: okCount, issues: issueCount, pending: pendingCount, inProgress: inProgressCount, total: totalChecks, teams: teams.length }
    }, [teams])

    const filteredTeams = useMemo(() => {
        let list = teams
        const q = search.trim().toLowerCase()
        if (q) list = list.filter((team) => (team.teamName ?? '').toLowerCase().includes(q))
        if (filter === 'issues') list = list.filter((team) => (team.checks ?? []).some((c) => c.result === 'issue'))
        if (filter === 'ok') list = list.filter((team) => {
            const checks = team.checks ?? []
            if (checks.length === 0) return false
            return checks.every((c) => c.result === 'ok')
        })
        return list
    }, [teams, search, filter])

    const selectedTeam = useMemo(() => {
        if (selectedTeamId == null) return filteredTeams[0] ?? null
        const team = filteredTeams.find((t) => t.id === selectedTeamId)
        return team ?? filteredTeams[0] ?? null
    }, [filteredTeams, selectedTeamId])

    useEffect(() => {
        if (filteredTeams.length === 0) return
        const inList = selectedTeamId != null && filteredTeams.some((t) => t.id === selectedTeamId)
        if (!inList) setSelectedTeamId(filteredTeams[0].id)
    }, [filteredTeams, selectedTeamId])

    const getCheckStats = useCallback((team: MobilisationTeam) => {
        const checks = team.checks ?? []
        let ok = 0
        let issues = 0
        checks.forEach((c) => {
            if (c.result === 'ok') ok++
            else if (c.result === 'issue') issues++
        })
        return { ok, issues, pending: checks.length - ok - issues, total: checks.length }
    }, [])

    const getEditState = useCallback(
        (check: TechnicalCheckRow) => {
            const id = check.recoveryTaskId ?? check.id
            if (editState[id]) return editState[id]
            return { status: statusFromApi(check.status ?? 'No'), comments: check.comments ?? '' }
        },
        [editState]
    )

    const setCheckEdit = useCallback((check: TechnicalCheckRow, field: 'status' | 'comments', value: string) => {
        const id = check.recoveryTaskId ?? check.id
        setEditState((prev) => ({
            ...prev,
            [id]: { ...(prev[id] ?? { status: statusFromApi(check.status ?? 'No'), comments: check.comments ?? '' }), [field]: value },
        }))
    }, [])

    const handleSave = useCallback(
        (check: TechnicalCheckRow) => {
            const id = check.recoveryTaskId ?? check.id
            if (!setId || id <= 0) return
            const state = getEditState(check)
            saveMutation.mutate({ recoveryTaskId: id, action: statusToApi(state.status), comments: state.comments || undefined })
        },
        [setId, getEditState, saveMutation]
    )

    const hasUnsavedChanges = useCallback(
        (check: TechnicalCheckRow) => {
            const state = getEditState(check)
            return statusToApi(state.status) !== (check.status ?? 'No').trim() || state.comments !== (check.comments ?? '')
        },
        [getEditState]
    )

    /** Group checks by saved status (from API) so cards don't move until the user saves. */
    const getChecksByStatus = useCallback((team: MobilisationTeam) => {
        const checks = team.checks ?? []
        const no: TechnicalCheckRow[] = []
        const inProgress: TechnicalCheckRow[] = []
        const completed: TechnicalCheckRow[] = []
        const issue: TechnicalCheckRow[] = []
        checks.forEach((c) => {
            const s = (c.status ?? 'No').trim()
            if (s === 'In Progress') inProgress.push(c)
            else if (s === 'Completed') completed.push(c)
            else if (s === 'Issue') issue.push(c)
            else no.push(c)
        })
        return { no, inProgress, completed, issue }
    }, [])

    const hasIssues = stats.issues > 0

    return (
        <div className="space-y-0 -m-6">
            <header className="rounded-t-lg border-x border-t border-border bg-card px-6 py-5 text-foreground">
                <div>
                    <h1 className="text-xl font-semibold">{t('recoverRequests.mobilisation.technicalChecklist', 'Technical Checklist')}</h1>
                    <p className="mt-0.5 text-sm text-muted-foreground">
                        {t('recoverRequests.mobilisation.step4Description', 'Systematic verification of system integrity across all teams.')}
                    </p>
                </div>
                <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-5">
                    <div className="rounded-lg border bg-card p-6">
                        <div className="flex items-center space-x-2">
                            <CheckCircle className="h-5 w-5 text-muted-foreground" />
                            <div>
                                <p className="text-sm font-medium">{t('recoverRequests.mobilisation.ok', 'OK')}</p>
                                <p className="text-2xl font-bold">{stats.ok}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">{t('recoverRequests.mobilisation.checks', 'checks')}</p>
                            </div>
                        </div>
                    </div>
                    <div className="rounded-lg border bg-card p-6">
                        <div className="flex items-center space-x-2">
                            <XCircle className="h-5 w-5 text-muted-foreground" />
                            <div>
                                <p className="text-sm font-medium">{t('recoverRequests.mobilisation.issuesFound', 'Issues Found')}</p>
                                <p className="text-2xl font-bold">{stats.issues}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">{t('recoverRequests.mobilisation.checks', 'checks')}</p>
                            </div>
                        </div>
                    </div>
                    <div className="rounded-lg border bg-card p-6">
                        <div className="flex items-center space-x-2">
                            <ClipboardCheck className="h-5 w-5 text-muted-foreground" />
                            <div>
                                <p className="text-sm font-medium">{t('recoverRequests.mobilisation.pending', 'Pending')}</p>
                                <p className="text-2xl font-bold">{stats.pending}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">{t('recoverRequests.mobilisation.checks', 'checks')}</p>
                            </div>
                        </div>
                    </div>
                    <div className="rounded-lg border bg-card p-6">
                        <div className="flex items-center space-x-2">
                            <Activity className="h-5 w-5 text-muted-foreground" />
                            <div>
                                <p className="text-sm font-medium">{t('recoverRequests.mobilisation.inProgress', 'In Progress')}</p>
                                <p className="text-2xl font-bold">{stats.inProgress}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">{t('recoverRequests.mobilisation.checks', 'checks')}</p>
                            </div>
                        </div>
                    </div>
                    <div className="rounded-lg border bg-card p-6">
                        <div className="flex items-center space-x-2">
                            <AlertTriangle className="h-5 w-5 text-muted-foreground" />
                            <div>
                                <p className="text-sm font-medium">{t('recoverRequests.mobilisation.totalChecks', 'Total Checks')}</p>
                                <p className="text-2xl font-bold">{stats.total}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">{t('recoverRequests.mobilisation.teams', 'Teams')} {stats.teams}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {hasIssues && onOpenImpactAssessment && (
                <div className="border-x border-border bg-card px-4 py-3">
                    <Alert variant="destructive">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            {t('recoverRequests.mobilisation.issuesDetected', 'Issues have been detected.')}{' '}
                            <button type="button" className="underline font-medium" onClick={onOpenImpactAssessment}>
                                {t('recoverRequests.mobilisation.goToImpactAssessment', 'Go to Impact Assessment (Step 5)')}
                            </button>
                        </AlertDescription>
                    </Alert>
                </div>
            )}

            <div className="flex flex-wrap items-center gap-3 border-x border-b border-border bg-muted/30 px-4 py-3">
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input placeholder={t('recoverRequests.mobilisation.searchTeams', 'Search teams...')} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
                </div>
                <div className="flex rounded-md border border-border bg-background p-0.5">
                    {(['all', 'issues', 'ok'] as const).map((f) => (
                        <button
                            key={f}
                            type="button"
                            onClick={() => setFilter(f)}
                            className={cn('rounded px-3 py-1.5 text-sm font-medium transition-colors', filter === f ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground')}
                        >
                            {f === 'all' && t('recoverRequests.mobilisation.allTeams', 'All Teams')}
                            {f === 'issues' && t('recoverRequests.mobilisation.hasIssues', 'Has Issues')}
                            {f === 'ok' && t('recoverRequests.mobilisation.allOk', 'All OK')}
                        </button>
                    ))}
                </div>
            </div>

            {filteredTeams.length === 0 ? (
                <div className="rounded-b-lg border-x border-b border-border bg-card p-8 text-center text-sm text-muted-foreground">
                    {t('recoverRequests.mobilisation.noTeams', 'No teams found.')}
                </div>
            ) : (
                <div className="rounded-b-lg border-x border-b border-border bg-card flex overflow-hidden min-h-[420px]">
                    <TechnicalChecklistSidebar filteredTeams={filteredTeams} selectedTeamId={selectedTeamId} onSelectTeam={setSelectedTeamId} getCheckStats={getCheckStats} t={t} />
                    <div className="flex-1 flex flex-col min-w-0 p-4">
                        {selectedTeam ? (
                            <>
                                <div className="shrink-0 flex flex-wrap items-center justify-between gap-3 pb-3 text-sm">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-medium text-foreground">{selectedTeam.teamName ?? '—'}</span>
                                        {(() => {
                                            const { no, inProgress, completed, issue } = getChecksByStatus(selectedTeam)
                                            return (
                                                <>
                                                    <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                                        {no.length} {t('recoverRequests.mobilisation.pending', 'Pending')}
                                                    </span>
                                                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
                                                        {inProgress.length} {t('recoverRequests.mobilisation.inProgress', 'In Progress')}
                                                    </span>
                                                    <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/50 dark:text-green-300">
                                                        {completed.length} {t('recoverRequests.mobilisation.completed', 'Completed')}
                                                    </span>
                                                    <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900/50 dark:text-red-300">
                                                        {issue.length} {t('recoverRequests.mobilisation.issuesFound', 'Issues')}
                                                    </span>
                                                </>
                                            )
                                        })()}
                                    </div>
                                    <CardTableViewToggle
                                        value={viewMode}
                                        onValueChange={setViewMode}
                                        order="table-first"
                                        tableLabel={t('recoverRequests.mobilisation.list', 'List')}
                                        cardLabel={t('recoverRequests.mobilisation.board', 'Board')}
                                        tableIcon={ListChecks}
                                        iconClassName="h-4 w-4 text-primary"
                                    />
                                </div>
                                {viewMode === 'table' && (
                                <div className="flex-1 overflow-auto rounded-md border border-border w-full min-w-0 overflow-hidden font-sans antialiased">
                                    <Table className="w-full min-w-full text-sm font-sans" style={{ tableLayout: 'fixed' }}>
                                        <TableHeader>
                                            <TableRow className="bg-table-header border-b border-table-border">
                                                <TableHead className="w-12 shrink-0 font-semibold text-foreground" style={{ paddingTop: 'var(--table-header-padding-y)', paddingBottom: 'var(--table-header-padding-y)', paddingLeft: 'var(--table-header-padding-x)', paddingRight: 'var(--table-header-padding-x)' }}>#</TableHead>
                                                <TableHead className="min-w-[240px] font-semibold text-foreground" style={{ paddingTop: 'var(--table-header-padding-y)', paddingBottom: 'var(--table-header-padding-y)', paddingLeft: 'var(--table-header-padding-x)', paddingRight: 'var(--table-header-padding-x)' }}>{t('recoverRequests.mobilisation.check', 'Check')}</TableHead>
                                                <TableHead className="min-w-[140px] w-[140px] shrink-0 font-semibold text-foreground" style={{ paddingTop: 'var(--table-header-padding-y)', paddingBottom: 'var(--table-header-padding-y)', paddingLeft: 'var(--table-header-padding-x)', paddingRight: 'var(--table-header-padding-x)' }}>{t('recoverRequests.mobilisation.status', 'Status')}</TableHead>
                                                <TableHead className="min-w-[280px] font-semibold text-foreground" style={{ paddingTop: 'var(--table-header-padding-y)', paddingBottom: 'var(--table-header-padding-y)', paddingLeft: 'var(--table-header-padding-x)', paddingRight: 'var(--table-header-padding-x)' }}>{t('recoverRequests.mobilisation.comments', 'Comments')}</TableHead>
                                                <TableHead className="min-w-[80px] w-[100px] shrink-0 font-semibold text-foreground" style={{ paddingTop: 'var(--table-header-padding-y)', paddingBottom: 'var(--table-header-padding-y)', paddingLeft: 'var(--table-header-padding-x)', paddingRight: 'var(--table-header-padding-x)' }}>{t('recoverRequests.mobilisation.user', 'User')}</TableHead>
                                                {canEdit && (
                                                    <>
                                                        <TableHead className="shrink-0 text-right font-semibold text-foreground" style={{ paddingTop: 'var(--table-header-padding-y)', paddingBottom: 'var(--table-header-padding-y)', paddingLeft: 'var(--table-header-padding-x)', paddingRight: 'var(--table-header-padding-x)', width: 72, minWidth: 72 }}>{t('common.actions', 'Actions')}</TableHead>
                                                        <TableHead className="w-12 shrink-0 text-right font-semibold text-foreground" style={{ paddingTop: 'var(--table-header-padding-y)', paddingBottom: 'var(--table-header-padding-y)', paddingLeft: 'var(--table-header-padding-x)', paddingRight: 'var(--table-header-padding-x)' }}></TableHead>
                                                    </>
                                                )}
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {(selectedTeam.checks ?? []).length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={canEdit ? 7 : 5} className="text-muted-foreground text-center py-6">—</TableCell>
                                                </TableRow>
                                            ) : (
                                                (selectedTeam.checks ?? []).map((check, idx) => {
                                                    const taskId = check.recoveryTaskId ?? check.id
                                                    const isSaving = saveMutation.isPending && saveMutation.variables?.recoveryTaskId === taskId
                                                    const state = getEditState(check)
                                                    return (
                                                        <TableRow key={check.id} className="hover:bg-table-row-hover border-b border-table-border font-sans">
                                                            <TableCell className="font-mono align-middle text-sm" style={{ paddingTop: 'var(--table-cell-padding-y)', paddingBottom: 'var(--table-cell-padding-y)', paddingLeft: 'var(--table-cell-padding-x)', paddingRight: 'var(--table-cell-padding-x)' }}>
                                                                <span className="inline-flex h-6 w-6 items-center justify-center rounded bg-muted text-xs">{idx + 1}</span>
                                                            </TableCell>
                                                            <TableCell className="font-medium text-foreground text-sm" style={{ paddingTop: 'var(--table-cell-padding-y)', paddingBottom: 'var(--table-cell-padding-y)', paddingLeft: 'var(--table-cell-padding-x)', paddingRight: 'var(--table-cell-padding-x)' }}>{check.check}</TableCell>
                                                            <TableCell className="min-w-[140px] w-[140px] shrink-0 text-sm" style={{ paddingTop: 'var(--table-cell-padding-y)', paddingBottom: 'var(--table-cell-padding-y)', paddingLeft: 'var(--table-cell-padding-x)', paddingRight: 'var(--table-cell-padding-x)' }}>
                                                                <span className="inline-flex">
                                                                <span className="inline-flex">
                                                                    {canEdit ? (
                                                                        <Select value={state.status} onValueChange={(v) => setCheckEdit(check, 'status', v)}>
                                                                            <SelectTrigger className={cn('inline-flex w-fit min-w-0 h-auto min-h-0 rounded-full border-0 px-2.5 py-0.5 text-xs font-medium whitespace-nowrap focus:ring-2', getStatusPillClass(state.status ?? 'Pending'))}>
                                                                                <SelectValue />
                                                                            </SelectTrigger>
                                                                            <SelectContent>
                                                                                {STATUS_OPTIONS.map((opt) => (
                                                                                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                                                                ))}
                                                                            </SelectContent>
                                                                        </Select>
                                                                    ) : (
                                                                        <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap', getStatusPillClass(check.status ?? 'No'))}>{statusFromApi(check.status ?? 'No')}</span>
                                                                    )}
                                                                </span>
                                                                </span>
                                                            </TableCell>
                                                            <TableCell className="min-w-[280px] text-sm" style={{ paddingTop: 'var(--table-cell-padding-y)', paddingBottom: 'var(--table-cell-padding-y)', paddingLeft: 'var(--table-cell-padding-x)', paddingRight: 'var(--table-cell-padding-x)' }}>
                                                                {canEdit ? (
                                                                    <Textarea className="min-h-[60px] w-full min-w-0 resize-y text-sm font-sans" value={state.comments} onChange={(e) => setCheckEdit(check, 'comments', e.target.value)} placeholder={t('recoverRequests.mobilisation.commentsPlaceholder', 'Comments')} rows={2} />
                                                                ) : (
                                                                    <span className="text-sm text-muted-foreground">{check.comments ?? '—'}</span>
                                                                )}
                                                            </TableCell>
                                                            <TableCell className="min-w-[80px] w-[100px] shrink-0 text-sm text-muted-foreground" style={{ paddingTop: 'var(--table-cell-padding-y)', paddingBottom: 'var(--table-cell-padding-y)', paddingLeft: 'var(--table-cell-padding-x)', paddingRight: 'var(--table-cell-padding-x)' }}>
                                                                {check.completedBy ?? '—'}
                                                            </TableCell>
                                                            {canEdit && (
                                                                <>
                                                                    <TableCell className="w-[72px] min-w-[72px] shrink-0 text-right text-sm" style={{ paddingTop: 'var(--table-cell-padding-y)', paddingBottom: 'var(--table-cell-padding-y)', paddingLeft: 'var(--table-cell-padding-x)', paddingRight: 'var(--table-cell-padding-x)', width: 72, minWidth: 72 }}>
                                                                        {hasUnsavedChanges(check) ? (
                                                                            <Button size="sm" variant="default" onClick={() => handleSave(check)} disabled={isSaving}>{t('common.save', 'Save')}</Button>
                                                                        ) : (
                                                                            <span className="inline-block h-8 w-[56px]" aria-hidden />
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell className="w-12 shrink-0 text-right text-sm" style={{ paddingTop: 'var(--table-cell-padding-y)', paddingBottom: 'var(--table-cell-padding-y)', paddingLeft: 'var(--table-cell-padding-x)', paddingRight: 'var(--table-cell-padding-x)' }}>
                                                                        <TechnicalTaskActionsIconButton setId={setId ?? null} recoveryTaskId={taskId} onClick={() => setActionsTaskId(taskId)} title={t('recoverRequests.mobilisation.taskActions', 'Task actions')} />
                                                                    </TableCell>
                                                                </>
                                                            )}
                                                        </TableRow>
                                                    )
                                                })
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                                )}
                                {viewMode === 'card' && (
                                <div className="flex-1 flex flex-col min-w-0 overflow-hidden p-4">
                                    <div className="flex-1 grid grid-cols-4 gap-2 overflow-auto min-w-0">
                                        {(() => {
                                            const { no, inProgress, completed, issue } = getChecksByStatus(selectedTeam)
                                            const cols = [
                                                { key: 'no', label: t('recoverRequests.mobilisation.pending', 'Pending'), tasks: no, dotClass: 'bg-slate-400', topBorderClass: 'border-t-slate-400', countClass: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
                                                { key: 'inProgress', label: t('recoverRequests.mobilisation.inProgress', 'In Progress'), tasks: inProgress, dotClass: 'bg-blue-500', topBorderClass: 'border-t-blue-500', countClass: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' },
                                                { key: 'completed', label: t('recoverRequests.mobilisation.completed', 'Completed'), tasks: completed, dotClass: 'bg-green-500', topBorderClass: 'border-t-green-500', countClass: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' },
                                                { key: 'issue', label: t('recoverRequests.mobilisation.issue', 'Issue'), tasks: issue, dotClass: 'bg-red-500', topBorderClass: 'border-t-red-500', countClass: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' },
                                            ]
                                            return cols.map((col) => (
                                                <div key={col.key} className={cn('flex flex-col rounded-lg border border-border bg-slate-50 dark:bg-slate-800/30 min-w-0 border-t-4 overflow-hidden', col.topBorderClass)}>
                                                    <div className="shrink-0 flex items-center justify-between px-3 py-2 border-b border-border bg-muted/30">
                                                        <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                                                            <span className={cn('h-2 w-2 shrink-0 rounded-full', col.dotClass)} />
                                                            {col.label}
                                                        </span>
                                                        <span className={cn('rounded-full px-2 py-0.5 font-mono text-xs font-medium', col.countClass)}>
                                                            {col.tasks.length}
                                                        </span>
                                                    </div>
                                                    <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-3 min-h-[200px]">
                                                        {col.tasks.length === 0 ? (
                                                            <p className="py-4 text-center text-xs text-muted-foreground">{t('recoverRequests.mobilisation.noChecks', 'No checks')}</p>
                                                        ) : (
                                                            col.tasks.map((check) => (
                                                                <ChecklistBoardCard
                                                                    key={check.recoveryTaskId ?? check.id}
                                                                    check={check}
                                                                    getEditState={getEditState}
                                                                    setCheckEdit={setCheckEdit}
                                                                    handleSave={handleSave}
                                                                    hasUnsavedChanges={hasUnsavedChanges}
                                                                    saveMutation={saveMutation}
                                                                    canEdit={canEdit}
                                                                    setActionsTaskId={setActionsTaskId}
                                                                    getStatusPillClass={getStatusPillClass}
                                                                    t={t}
                                                                    setId={setId ?? null}
                                                                />
                                                            ))
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        })()}
                                    </div>
                                </div>
                                )}
                            </>
                        ) : (
                            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
                                {t('recoverRequests.mobilisation.selectTeam', 'Select a team')}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {actionsTaskId != null && setId != null && (
                <TechnicalTaskActionsDialog setId={setId} recoveryTaskId={actionsTaskId} onClose={() => setActionsTaskId(null)} />
            )}
        </div>
    )
}

function TechnicalTaskActionsDialog({
    setId,
    recoveryTaskId,
    onClose,
}: {
    setId: number
    recoveryTaskId: number
    onClose: () => void
}) {
    const { t } = useTranslation()
    const { data: actions = [], isLoading } = useTechnicalTaskActionsBySet(setId, recoveryTaskId)

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>
                        {t('recoverRequests.mobilisation.taskActionsTitle', 'Task actions')}
                    </DialogTitle>
                </DialogHeader>
                <div className="max-h-[60vh] overflow-auto">
                    {isLoading ? (
                        <p className="text-sm text-muted-foreground py-4">{t('common.loading', 'Loading...')}</p>
                    ) : actions.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-4">
                            {t('recoverRequests.mobilisation.noTaskActions', 'No actions recorded.')}
                        </p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[120px]">{t('recoverRequests.mobilisation.status', 'Status')}</TableHead>
                                    <TableHead className="min-w-[280px]">{t('recoverRequests.mobilisation.comments', 'Comments')}</TableHead>
                                    <TableHead>{t('recoverRequests.mobilisation.statusDateAndTime', 'Status date and time')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {actions.map((a) => (
                                    <TableRow key={a.id}>
                                        <TableCell className="w-[120px] align-top">
                                            <span
                                                className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusPillClass(a.action)}`}
                                            >
                                                {a.action}
                                            </span>
                                        </TableCell>
                                        <TableCell className="min-w-[280px] align-top">{a.comments || '—'}</TableCell>
                                        <TableCell className="align-top">
                                            <span className="whitespace-nowrap">
                                                {a.userName != null && a.userName !== '' && (
                                                    <>
                                                        <span className="text-muted-foreground font-medium">{a.userName}</span>
                                                        <span className="text-muted-foreground/70 mx-1.5">·</span>
                                                    </>
                                                )}
                                                {a.statusdatetime
                                                    ? new Date(a.statusdatetime).toLocaleString()
                                                    : '—'}
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}
